import { useEffect, useState, useCallback, useRef, Component } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { api } from '../api';
import { useApp } from '../contexts/AppContext';
import { caseTypeLabel } from '../constants/caseTypes';
import ExplorationTab from './tabs/ExplorationTab';
import ChronologyTab from './tabs/ChronologyTab';
import WitnessesTab from './tabs/WitnessesTab';
import UploadTab from './tabs/UploadTab';
import InventoryTab from './tabs/InventoryTab';
import ChecklistTab from './tabs/ChecklistTab';
import SettingsTab from './tabs/SettingsTab';
import UsageMeter from '../components/UsageMeter';
import GraphDeepLink from '../components/graph/GraphDeepLink';

/* ── ErrorBoundary — catch tab-level crashes ── */
class TabErrorBoundary extends Component {
  state = { error: null, errorInfo: null };
  static getDerivedStateFromError(error) { return { error }; }
  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });
    console.error('[TabErrorBoundary]', error.message, error.stack);
  }
  componentDidUpdate(prevProps) {
    if (prevProps.tabKey !== this.props.tabKey) this.setState({ error: null, errorInfo: null });
  }
  render() {
    if (this.state.error) {
      return (
        <div className="error-page" style={{ padding: '2rem' }}>
          <h3>Something went wrong</h3>
          <p>{this.state.error.message}</p>
          {this.state.error.stack && (
            <pre style={{ fontSize: '0.75rem', whiteSpace: 'pre-wrap', maxHeight: '200px', overflow: 'auto', background: '#f5f5f5', padding: '1rem', borderRadius: '8px', marginTop: '1rem', textAlign: 'left' }}>
              {this.state.error.stack}
            </pre>
          )}
          <button className="btn" onClick={() => this.setState({ error: null, errorInfo: null })} style={{ marginTop: '1rem' }}>
            Try Again
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

const TABS = [
  { key: 'upload',      label: 'Upload & Processing' },
  { key: 'checklist',   label: 'Discovery Check' },
  { key: 'inventory',   label: 'Document Inventory' },
  { key: 'exploration', label: 'Ask Arc' },
  { key: 'chronology',  label: 'Chronology' },
  { key: 'witnesses',   label: 'Witnesses' },
  { key: 'settings',    label: 'Case Settings' },
];

export default function CaseDetail() {
  const { caseId, tab } = useParams();
  const navigate = useNavigate();
  const { caseData, dispatch } = useApp();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const activeTab = tab || 'upload';
  const [uploadActive, setUploadActive] = useState(false);
  const uploadActiveRef = useRef(false);

  const handleUploadStart = useCallback(() => {
    setUploadActive(true);
    uploadActiveRef.current = true;
  }, []);
  const handleUploadComplete = useCallback(() => {
    setUploadActive(false);
    uploadActiveRef.current = false;
  }, []);

  // Warn on actual page-close/refresh when uploads are running.
  // (SPA tab-switching is now safe because UploadTab stays mounted.)
  useEffect(() => {
    function onBeforeUnload(e) {
      if (!uploadActiveRef.current) return;
      e.preventDefault();
      e.returnValue = 'Uploads are still in progress. If you leave, the current batch will be lost.';
    }
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, []);

  // Load case data when caseId changes
  useEffect(() => {
    let cancelled = false;

    async function loadCase() {
      setLoading(true);
      setError(null);
      try {
        // Fire case metadata + aggregated dashboard in parallel — they're
        // independent. The dashboard endpoint collapses what used to be 8
        // separate case-scoped GETs (inventory, statistics, folder-tree,
        // stages summary, upload progress, background jobs, checklist
        // match/profile active) into one backend session. See Pool
        // Governance P2.
        const [caseRes, dashboardRes] = await Promise.allSettled([
          api.get(`/cases/${caseId}`),
          api.get(`/cases/${caseId}/dashboard`),
        ]);
        if (cancelled) return;

        if (caseRes.status !== 'fulfilled') {
          throw new Error(caseRes.reason?.message || 'Failed to load case');
        }
        const data = caseRes.value;
        dispatch({ type: 'SET_CASE', id: caseId, data });

        if (dashboardRes.status === 'fulfilled') {
          const dashboard = dashboardRes.value;
          dispatch({ type: 'SET_CASE_DASHBOARD', data: dashboard });
          // Mirror to existing context keys so consumers that already
          // read state.documents / state.folderTree need no change.
          dispatch({ type: 'SET_DOCUMENTS', docs: dashboard.documents?.documents || [] });
          dispatch({ type: 'SET_FOLDER_TREE', tree: dashboard.folder_tree });
        } else {
          // Soft-fail: page still loads with case header even if the
          // dashboard envelope fails. Tabs will refetch on their own
          // poll loops.
          console.warn('case dashboard fetch failed', dashboardRes.reason);
        }

        // Pricing is org-scoped and not part of /dashboard; fetch only
        // if the case has an organization.
        if (data.organization_id) {
          try {
            const pricing = await api.get(`/pricing-config/${data.organization_id}`);
            if (!cancelled) dispatch({ type: 'SET_PRICING', pricing });
          } catch (err) {
            console.warn('pricing fetch failed', err);
          }
        }
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadCase();
    return () => { cancelled = true; };
  }, [caseId, dispatch]);

  function switchTab(key) {
    navigate(`/case/${caseId}/${key}`, { replace: true });
  }

  if (loading) {
    return <div className="loading">Loading case…</div>;
  }

  if (error) {
    return (
      <div className="error-page">
        <h2>Error loading case</h2>
        <p>{error}</p>
        <Link to="/cases" className="btn">← Back to Cases</Link>
      </div>
    );
  }

  return (
    <div className="case-detail">
      {/* Case header */}
      <div className="case-header">
        <Link to="/cases" className="back-link">← Cases</Link>
        <div className="case-header-info">
          <h1>{caseData?.name || 'Case'}</h1>
          <div className="case-meta">
            {caseData?.case_number && <span className="case-number">#{caseData.case_number}</span>}
            <span className="badge badge-type">{caseTypeLabel(caseData?.case_type)}</span>
            <span>{caseData?.document_count || 0} documents</span>
            {caseId && (
              <UsageMeter
                scope="case"
                caseId={caseId}
                variant="compact"
              />
            )}
          </div>
        </div>
      </div>

      {caseData?.access === 'consultant' && (
        <div className="alert" role="note" style={{ margin: '8px 0', fontSize: 13 }}>
          You are working on this case as an outside consultant for <strong>{caseData.organization_name || 'another organization'}</strong>
          {caseData.grant_expires_at && <> · access until {new Date(caseData.grant_expires_at).toLocaleDateString()}</>}.
          Everything you do here is recorded under your name in their audit log.
        </div>
      )}

      {/* Tab bar */}
      <div className="tab-bar">
        {TABS.map(t => (
          <button
            key={t.key}
            className={`tab ${activeTab === t.key ? 'tab-active' : ''}`}
            onClick={() => switchTab(t.key)}
          >
            {t.label}
            {t.key === 'upload' && uploadActive && activeTab !== 'upload' && (
              <span className="tab-upload-badge" title="Upload in progress">⬆</span>
            )}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {/*
        UploadTab and ExplorationTab are ALWAYS mounted (never conditionally
        rendered) so that:
        - Upload's iframe / in-memory file queue survive tab switches.
        - Ask Arc keeps polling chat events while the user views other tabs,
          so an in-flight answer continues streaming in the background and
          the UI looks unchanged when the user returns to the tab.
        Other tabs are rendered lazily (only when first activated).
      */}
      <div className="tab-content">
        <div style={{ display: activeTab === 'upload' ? undefined : 'none' }}>
          <UploadTab
            caseId={caseId}
            onUploadStart={handleUploadStart}
            onUploadComplete={handleUploadComplete}
          />
        </div>
        <div style={{ display: activeTab === 'exploration' ? undefined : 'none' }}>
          <TabErrorBoundary tabKey="exploration">
            <ExplorationTab caseId={caseId} />
          </TabErrorBoundary>
        </div>
        <TabErrorBoundary tabKey={activeTab}>
          {activeTab === 'checklist' && <ChecklistTab caseId={caseId} />}
          {activeTab === 'inventory' && <InventoryTab caseId={caseId} />}
          {activeTab === 'chronology' && <ChronologyTab caseId={caseId} />}
          {activeTab === 'witnesses' && <WitnessesTab caseId={caseId} />}
          {activeTab === 'settings' && <SettingsTab caseId={caseId} />}
        </TabErrorBoundary>
        <GraphDeepLink caseId={caseId} />
      </div>
    </div>
  );
}
